import React from "react";
import styled from "styled-components";
import {
  Body,
  elevation01,
  primary01,
  semanticBlue,
  semanticRed01,
  semanticRed02,
  shade0,
  shade500,
  shade600,
  shade800,
  shade850,
  shade900,
  shade950,
} from "@songtradr-one/components-frontend";
import { staticText } from "../../statics/static-text";

// TEXT
export const text = staticText.serverTimingHud;

// STYLES
export const FloatingButton = styled.button<{
  $isOpen: boolean;
  $hasItems: boolean;
}>`
  position: fixed;
  right: 0.5rem;
  bottom: 0.5rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${shade800};
  color: ${shade0};
  cursor: pointer;
  box-shadow: ${elevation01};
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover {
    background: ${shade600};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Minified dot – uses a transparent padding box for a larger click target
// while keeping the visible dot at 0.5rem. Offset from the corner to avoid
// the browser's native resize handle (~6px).
export const MinifiedDot = styled.button`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  width: 0.25rem;
  height: 0.25rem;
  padding: 0.25rem;
  border: solid 1px ${primary01};
  border-radius: 2px;
  background: ${primary01};
  cursor: pointer;
  z-index: 9999;
  opacity: 0.5;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 1;
  }
`;

// Badge to show count
export const CountBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: ${primary01};
  color: ${shade0};
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 2px ${shade0};
`;

export const Panel = styled.div`
  position: fixed;
  right: 0.5rem;
  bottom: 64px;
  width: min(96vw, 700px);
  max-width: 700px;
  background: ${shade850};
  border-radius: 12px;
  box-shadow: ${elevation01};
  padding: 2rem;
  color: ${shade0};
  z-index: 9999;
  min-height: 85vh;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
`;

export const PanelTitle = styled(Body)`
  font-weight: 600;
`;

export const PanelTimestamp = styled(Body).attrs({ variant: "small" })`
  margin-left: 1rem;
  color: ${shade500};
`;

export const PanelCounter = styled(Body).attrs({ variant: "small" })`
  color: ${shade500};
`;

export const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const TimingContainer = styled.div`
  overflow: auto;
  padding-right: 2px;
`;

export const TimingEntry = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    "name bar dur"
    ".    desc .";
  column-gap: 0.25rem;
  row-gap: 4px;
  align-items: center;
  margin: 0.25rem 0;
`;

export const TimingName = styled.div`
  grid-area: name;
  display: flex;
  align-items: center;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  color: ${shade0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SlowMarkerPill = styled.span`
  margin-left: 0.25rem;
  font-size: 10px;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  background: ${semanticRed01};
  color: ${shade950};
`;

export const TimingTrack = styled.div`
  grid-area: bar;
  position: relative;
  height: 10px;
  border-radius: 6px;
  background: ${shade900};
  overflow: hidden;
  align-self: center;
`;

export const TimingBar = styled.div<{ $width: number; $isSlow?: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $width }) => $width}%;
  border-radius: 6px;
  background: ${({ $isSlow }) => ($isSlow ? semanticRed02 : semanticBlue)};
  opacity: 0.8;
`;

export const TimingDuration = styled.div`
  grid-area: dur;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${shade0};
  white-space: nowrap;
`;

export const TimingDescription = styled(Body).attrs({ variant: "small" })`
  grid-area: desc;
  align-self: start;
  font-size: 11px;
  color: ${shade500};
  line-height: 1.3;
`;

export const JsonContainer = styled.pre`
  flex: 1;
  min-height: 0;
  overflow: auto;
  margin: 0;
  margin-top: 0.5rem;
  background: ${shade950};
  color: ${shade0};
  border-radius: 4px;
  padding: 1rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.45;
  box-shadow: inset 0 0 0 1px ${shade800};
`;

export const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.25rem;
  margin-top: 2rem;
`;

export const EmptyState = styled(Body)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${shade500};
`;

// DATA

// TYPES
export type Item = {
  id: string;
  text: string;
  raw?: string | null;
  ts: number;
  timeout?: number;
  json?: unknown;
};

export type Group = {
  key: string;
  text: string;
  raw?: string | null;
  latestTs: number;
  items: Item[];
};

export type ServerTimingEntry = {
  name: string;
  dur?: number;
  desc?: string;
  isSlow?: boolean;
};

export type TelemetryMessage = {
  kind: string;
  text: string;
  raw?: string | null;
  jsonResult?: unknown;
};

// CONSTANTS

// FUNCTIONS
export function parseServerTiming(raw?: string | null): ServerTimingEntry[] {
  if (!raw) return [];
  const parts = raw.match(/(?:[^,"\\]|\\.|"(?:[^"\\]|\\.)*")+/g) || [];
  return parts.map((p) => {
    const [head, ...params] = p.trim().split(";");

    // detect ".slow" suffix and strip it for display
    const slowSuffix = /\.slow\b/i.test(head);
    const displayHead = head.replace(/\.slow\b/i, "");
    const entry: ServerTimingEntry = {
      name: displayHead || "unknown",
      isSlow: slowSuffix,
    };

    for (const param of params) {
      const [k, vRaw] = param.split("=").map((s) => s.trim());
      if (!k) continue;
      const key = k.toLowerCase();
      if (key === "dur") {
        const n = Number(vRaw?.replace(/(^")|("$)/g, ""));
        if (!Number.isNaN(n)) entry.dur = n;
      } else if (key === "desc") {
        entry.desc = vRaw?.replace(/^"(.*)"$/, "$1");
        if (!entry.isSlow && /(^|[\s-])slow([^\w]|$)/i.test(entry.desc || "")) {
          entry.isSlow = true;
        }
      }
    }
    return entry;
  });
}

export function prettyJSON(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// ASSETS
export const BoltIcon = (props: React.ComponentPropsWithRef<"svg">) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);
